import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isProduction } from '../config/environment';

const DebugToggle = () => {
  const location = useLocation();
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [showSectionBorders, setShowSectionBorders] = useState(false);
  const [showSectionLabels, setShowSectionLabels] = useState(false);
  const [showContainerBorders, setShowContainerBorders] = useState(false);
  const [showContainerLabels, setShowContainerLabels] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Get page name from current location
  const getPageName = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path === '/ki-kurse') return 'ki-kurse';
    if (path === '/ki-impulse') return 'ki-impulse';
    if (path === '/ki-werkstatt') return 'ki-werkstatt';
    if (path === '/kontakt') return 'kontakt';

    if (path === '/login') return 'login';
    if (path === '/impressum') return 'impressum';
    if (path === '/datenschutz') return 'datenschutz';
    // Remove leading slash and convert to lowercase
    return path.replace('/', '').toLowerCase() || 'unknown';
  };

  // Set page name as data attribute on body
  useEffect(() => {
    const pageName = getPageName();
    document.body.setAttribute('data-page', pageName);
    console.log('Debug: Page name set to', pageName);
  }, [location.pathname]);

  useEffect(() => {
    console.log('DebugToggle component mounted');
    // Check localStorage and apply debug mode on load
    const savedDebugMode = localStorage.getItem('debugMode') === 'true';
    const savedSectionBorders = localStorage.getItem('debugSectionBorders') === 'true';
    const savedSectionLabels = localStorage.getItem('debugSectionLabels') === 'true';
    const savedContainerBorders = localStorage.getItem('debugContainerBorders') === 'true';
    const savedContainerLabels = localStorage.getItem('debugContainerLabels') === 'true';
    const savedCollapsed = localStorage.getItem('debugCollapsed') !== 'false'; // Default to true
    
    setIsDebugMode(savedDebugMode);
    setShowSectionBorders(savedSectionBorders);
    setShowSectionLabels(savedSectionLabels);
    setShowContainerBorders(savedContainerBorders);
    setShowContainerLabels(savedContainerLabels);
    setIsCollapsed(savedCollapsed);
    
    updateDebugClasses(savedDebugMode, savedSectionBorders, savedSectionLabels, savedContainerBorders, savedContainerLabels);
  }, []);

  const updateDebugClasses = (debug: boolean, sectionBorders: boolean, sectionLabels: boolean, containerBorders: boolean, containerLabels: boolean) => {
    // Remove all debug classes first
    document.body.classList.remove('debug', 'debug-section-borders', 'debug-section-labels', 'debug-container-borders', 'debug-container-labels');
    
    // Add classes based on current state
    if (debug) document.body.classList.add('debug');
    if (sectionBorders) document.body.classList.add('debug-section-borders');
    if (sectionLabels) document.body.classList.add('debug-section-labels');
    if (containerBorders) document.body.classList.add('debug-container-borders');
    if (containerLabels) document.body.classList.add('debug-container-labels');
  };

  const toggleDebug = () => {
    const newDebugMode = !isDebugMode;
    setIsDebugMode(newDebugMode);
    localStorage.setItem('debugMode', newDebugMode.toString());
    updateDebugClasses(newDebugMode, showSectionBorders, showSectionLabels, showContainerBorders, showContainerLabels);
  };

  const toggleSectionBorders = () => {
    const newValue = !showSectionBorders;
    setShowSectionBorders(newValue);
    localStorage.setItem('debugSectionBorders', newValue.toString());
    updateDebugClasses(isDebugMode, newValue, showSectionLabels, showContainerBorders, showContainerLabels);
  };

  const toggleSectionLabels = () => {
    const newValue = !showSectionLabels;
    setShowSectionLabels(newValue);
    localStorage.setItem('debugSectionLabels', newValue.toString());
    updateDebugClasses(isDebugMode, showSectionBorders, newValue, showContainerBorders, showContainerLabels);
  };

  const toggleContainerBorders = () => {
    const newValue = !showContainerBorders;
    setShowContainerBorders(newValue);
    localStorage.setItem('debugContainerBorders', newValue.toString());
    updateDebugClasses(isDebugMode, showSectionBorders, showSectionLabels, newValue, showContainerLabels);
  };

  const toggleContainerLabels = () => {
    const newValue = !showContainerLabels;
    setShowContainerLabels(newValue);
    localStorage.setItem('debugContainerLabels', newValue.toString());
    updateDebugClasses(isDebugMode, showSectionBorders, showSectionLabels, showContainerBorders, newValue);
  };

  const toggleCollapsed = () => {
    const newValue = !isCollapsed;
    setIsCollapsed(newValue);
    localStorage.setItem('debugCollapsed', newValue.toString());
  };

  const debugInfo = {
    'Red': 'Main page containers',
    'Green': 'Sections', 
    'Blue': 'Containers',
    'Orange': 'Header/Nav',
    'Purple': 'Footer',
    'Pink': 'Hero/Content',
    'Turquoise': 'Grids/Grid Items',
    'Gold': 'Forms',
    'Crimson': 'Buttons',
    'Lime': 'Logo containers',
    'Dark Orchid': 'Admin containers',
    'Medium Orchid': 'Admin header',
    'Plum': 'Admin content',
    'Lime Green': 'Partners section',
    'Light Green': 'Partners container',
    'Pale Green': 'Partners grid',
    'Green Yellow': 'Partner items'
  };

  const tooltipInfo = [
    'Hover over elements for detailed info',
    'data-debug-info: Advanced tooltips',
    'data-debug: Simple labels',
    'Borders show component structure',
    'Colors indicate element types',
    'Hover states show with dashed red outline',
    'Partner items lift on hover (gold border)',
    'Buttons change color on hover (tomato)'
  ];

  // Don't render debug toggle in production/main environment
  if (isProduction()) {
    return null;
  }

  return (
    <>
      <div style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        zIndex: 9999,
        background: 'rgba(0, 0, 0, 0.9)',
        color: 'white',
        padding: isCollapsed ? '8px' : '12px',
        borderRadius: isCollapsed ? '50%' : '8px',
        fontSize: '12px',
        fontFamily: 'monospace',
        minWidth: isCollapsed ? 'auto' : '200px',
        width: isCollapsed ? '36px' : 'auto',
        height: isCollapsed ? '36px' : 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }}
      onMouseEnter={(e) => {
        if (isCollapsed) {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.95)';
        }
      }}
      onMouseLeave={(e) => {
        if (isCollapsed) {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
        }
      }}>
        <div 
          style={{ 
            marginBottom: isCollapsed ? '0' : '8px', 
            fontWeight: 'bold', 
            fontSize: isCollapsed ? '16px' : '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'space-between',
            width: '100%'
          }}
          onClick={toggleCollapsed}
        >
          {isCollapsed ? (
            <span 
              style={{
                position: 'relative',
                display: 'inline-block'
              }}
              title={
                (isDebugMode || showSectionBorders || showSectionLabels || showContainerBorders || showContainerLabels) ? 
                `Debug Active: ${[
                  isDebugMode && 'Global Debug',
                  showSectionBorders && 'Section Borders',
                  showSectionLabels && 'Section Labels', 
                  showContainerBorders && 'Container Borders',
                  showContainerLabels && 'Container Labels'
                ].filter(Boolean).join(', ')}` : 
                'Debug Inactive - Click to open controls'
              }
            >
              🐛
              {(isDebugMode || showSectionBorders || showSectionLabels || showContainerBorders || showContainerLabels) && (
                <span style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#00ff00',
                  borderRadius: '50%',
                  fontSize: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}></span>
              )}
            </span>
          ) : (
            <>
              🐛 Debug Controls
              <span style={{ 
                marginLeft: '8px', 
                fontSize: '12px',
                transform: 'rotate(180deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </>
          )}
        </div>
        
        {!isCollapsed && (
          <>
            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isDebugMode}
                  onChange={toggleDebug}
                  style={{ marginRight: '8px' }}
                />
                Global Debug Mode
              </label>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showSectionBorders}
                  onChange={toggleSectionBorders}
                  style={{ marginRight: '8px' }}
                />
                Section Borders
              </label>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showSectionLabels}
                  onChange={toggleSectionLabels}
                  style={{ marginRight: '8px' }}
                />
                Section Labels
              </label>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showContainerBorders}
                  onChange={toggleContainerBorders}
                  style={{ marginRight: '8px' }}
                />
                Container Borders
              </label>
            </div>

            <div>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showContainerLabels}
                  onChange={toggleContainerLabels}
                  style={{ marginRight: '8px' }}
                />
                Container Labels
              </label>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default DebugToggle;
